import { BookOpenText, CircleHelp, History, Undo2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  narrationContextCopy,
  storyFidelityDetailLines,
  type NarrationContextCopy,
  type StoryFidelity,
} from "./storyFidelity";

// ---------------------------------------------------------------------------
// NarrationContextBadge — the first thing on the Echo details page (CSF-034,
// contract §9): did this Echo's narration use the customer-story context
// (A/B treatment), or the old one (A/B control, a treatment that fell back
// after an authoring failure, or made before the system)? Shown for every
// Echo; the tooltip carries the fallback reason, experiment, rule, ledger
// status and must-say coverage.
// ---------------------------------------------------------------------------

const AMBER_TONE =
  "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100";

const TONE_CLASSES: Record<NarrationContextCopy["tone"], string> = {
  new: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100",
  control: AMBER_TONE,
  fallback: AMBER_TONE,
  old: "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100",
  unknown: "border-dashed bg-transparent text-muted-foreground",
};

const TONE_ICONS: Record<NarrationContextCopy["tone"], typeof History> = {
  new: BookOpenText,
  control: History,
  fallback: Undo2,
  old: History,
  unknown: CircleHelp,
};

export function NarrationContextBadge({
  fidelity,
  className,
}: {
  fidelity: StoryFidelity;
  className?: string;
}) {
  if (fidelity.kind === "loading") {
    return (
      <Skeleton
        data-testid="narration-context-badge"
        data-kind="loading"
        role="status"
        aria-busy="true"
        aria-label="Loading narration context"
        className={cn("h-8 w-72 max-w-full", className)}
      />
    );
  }

  const copy = narrationContextCopy(fidelity);
  const details = storyFidelityDetailLines(fidelity);
  const Icon = TONE_ICONS[copy.tone];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          tabIndex={0}
          role="status"
          data-testid="narration-context-badge"
          data-kind={fidelity.kind}
          aria-label={`${copy.title} · ${copy.subtitle}. ${details.join(". ")}`}
          className={cn(
            "h-auto max-w-full cursor-default flex-wrap justify-start gap-x-2 gap-y-0.5 whitespace-normal px-3 py-1.5 text-sm [&>svg]:size-4",
            TONE_CLASSES[copy.tone],
            className,
          )}
        >
          <Icon aria-hidden="true" />
          <span className="font-semibold">{copy.title}</span>
          <span className="text-xs font-normal opacity-80">
            {copy.subtitle}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className="max-w-sm space-y-0.5 bg-popover text-popover-foreground shadow-md"
      >
        {details.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
